import React from 'react';
import _ from 'lodash';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {connect} from 'react-redux';
import REST from 'go/requests/rest';
import {ConfigFragment} from 'componentLibrary/layout/dynamicFragment';
import {Contexts} from 'componentLibrary';
import {MODES} from 'common/requestDetails/constants.js';
import {calculateChildrenTotalByMode} from 'common/requestDetails/utils';
import {ContentDispatch} from 'framework';
const {EligibleItemsContext} = Contexts;
import {forceJsonResponse, MONO, FLUX} from 'common/utils';

export const getAllIds = (items) => {
  let ids = [];
  _.forEach(items, (item) => {
    if (item.itemCode) {
      ids.push(item.itemCode);
    }
    if (item.children) {
      ids = _.concat(ids, getAllIds(item.children));
    }
  });
  return ids;
};

export const filterConfiguration = (config, ineligibleItems) => {
  const streamsToMarkFullyIneligible = _.map(
    _.filter(ineligibleItems, (item) => item.ruleItemCodes === undefined),
    (stream) => stream.fundingStreamType
  );

  _.forEach(config.fundingStream, (stream) => {
    stream.ineligible = _.includes(
      streamsToMarkFullyIneligible,
      stream.fundingStreamType
    );
    _.forEach(stream.items, (item) => {
      // mutates item to add flag
      markItemEligibility(item, stream.fundingStreamType, ineligibleItems);
    });
  });

  return config;
};

export const markItemEligibility = (itemConfig, fundingStreamType, ineligibleItems) => {
  const ineligibleItem = _.find(ineligibleItems, (ineligible) => {
    return (
      _.includes(ineligible.ruleItemCodes, itemConfig.itemCode) &&
      ineligible.fundingStreamType === fundingStreamType
    );
  });
  itemConfig.ineligible = !!ineligibleItem;
  _.forEach(itemConfig.children, (childItem) =>
    markItemEligibility(childItem, fundingStreamType, ineligibleItems)
  );
};

export const transformItem = (item, itemRefData) => {
  const itemData = _.find(itemRefData, (ref) => ref.itemCode === item.itemCode);
  item.costType = _.get(itemData, 'costType');
  item.name = _.get(itemData, 'name');
  _.forEach(item.children, (child) => transformItem(child, itemRefData));
};

export const transformConfiguration = (config, itemRefData) => {
  _.forEach(config.fundingStream, (stream) => {
    _.forEach(stream.items, (item) => transformItem(item, itemRefData));
  });
  return config;
};

export const getTotalCostEstimateAmount = (fsData) => {
  const children = _.get(fsData, '[0].children', {});
  return calculateChildrenTotalByMode(children, MODES.APPLICATION);
};

const payloadFunMap = new Map();
payloadFunMap.set('getTotalCostEstimateAmount', getTotalCostEstimateAmount);

export class SubAwardRecommendationListener extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ineligibleItems: props.runRules ? null : [],
      refItems: null,
      previousRequestDetails: [],
    };
  }

  loadSubApplication() {
    const {subGrantId, refEntityType} = this.props;
    REST.dynamicData
      .getEntitiesBySubGrantIdAndEntityType(subGrantId, refEntityType)
      .then(REST.handler.handleResponse)
      .then((data) => {
        this.setState({
          previousRequestDetails: _.get(data, '0.payload.fundingStreams', []),
        });
      });
  }

  componentDidMount() {
    this.loadAndEvalRules(true);
    this.loadSubApplication();
  }

  componentDidUpdate(prevProps) {
    const didChange = !_.isEqual(
      _.get(this.props, 'versionId'),
      _.get(prevProps, 'versionId')
    );
    this.loadAndEvalRules(didChange);
  }

  loadAndEvalRules(didChange) {
    const {
      eligibilityConfig,
      data: eligibility,
      grantId,
      fundingOpportunityId,
      token,
      runRules,
      categoryId,
      rulePayload,
    } = this.props;
    const {refItems, ineligibleItems} = this.state;
    const hasDependenciesForRules =
      (categoryId !== undefined ||
        (grantId !== undefined && fundingOpportunityId !== undefined)) &&
      (ineligibleItems === null || didChange) &&
      !_.get(eligibilityConfig, 'loading', true);

    let rulesPromise = null;
    if (hasDependenciesForRules && runRules) {
      this.setState({
        ineligibleItems: false,
      });
      if (rulePayload !== undefined) {
        let payload = {};
        _.each(rulePayload, (value, key) => {
          if (typeof rulePayload[key] === 'object') {
            const {func, args, ifUndefined} = rulePayload[key];
            let params = [];
            _.each(args, (arg) => {
              params.push(_.get(this.props, arg));
            });
            payload[key] = payloadFunMap.get(func)(...params);
            if (payload[key] === undefined) {
              payload[key] = ifUndefined;
            }
          } else {
            payload[key] = _.get(this.props, value);
          }
        });
        rulesPromise = REST.rules
          .getRulesByPayload(token, payload)
          .then((res) => forceJsonResponse(res, FLUX))
          .then((ineligibleItems) => {
            this.setState({
              ineligibleItems: _.flatMap(ineligibleItems),
            });
          })
          .catch((error) => console.log(error));
      } else {
        rulesPromise = REST.rules
          .getIneligibleItems(token, grantId, fundingOpportunityId)
          .then((res) => forceJsonResponse(res, FLUX))
          .then((ineligibleItems) => {
            this.setState({
              ineligibleItems: _.flatMap(ineligibleItems),
            });
          })
          .catch((error) => console.log(error));
      }
    }

    let refsPromise = null;
    if (!_.get(eligibilityConfig, 'loading', true) && refItems === null) {
      const fundingStreams = _.get(eligibilityConfig, 'fundingStream');
      const ids = _.uniq(
        _.flatMap(_.map(fundingStreams, (stream) => getAllIds(stream.items)))
      );
      this.setState({
        refItems: false,
      });
      if (!_.isEmpty(ids)) {
        refsPromise = REST.ref
          .getItemsByItemCodes(this.props.token, ids)
          .then((res) => forceJsonResponse(res, FLUX))
          .then((itemData) => {
            this.setState({
              refItems: itemData,
            });
          })
          .catch((error) => console.log(error));
      }
    }

    if (rulesPromise && refsPromise) {
      Promise.all([rulesPromise, refsPromise]).then(() => {
        // TODO refactor so request details can load before this loads, but adding/editing item code is blocked
        this.filterEligibleStreams(eligibilityConfig);
      });
    } else if (rulesPromise || refsPromise) {
      let p = rulesPromise ? rulesPromise : refsPromise;
      p.then(() => {
        this.filterEligibleStreams(eligibilityConfig);
      });
    }
  }

  filterEligibleStreams(eligibilityConfig) {
    const config = JSON.parse(JSON.stringify(eligibilityConfig));
    const filteredConfiguration = filterConfiguration(config, this.state.ineligibleItems);
    const transformedConfiguration = transformConfiguration(
      filteredConfiguration,
      this.state.refItems
    );
    if (!_.isEqual(this.state.calculatedEligibilityConfig, transformedConfiguration)) {
      this.setState({
        calculatedEligibilityConfig: transformedConfiguration,
      });
    }
  }

  render() {
    const contextValue = {
      eligibilityConfig: this.state.calculatedEligibilityConfig,
      entityType: this.props.entityType,
      dataObject: this.props.dataObject,
    };

    const config = this.props.config.contents[0];

    const additionalProps = this.props.costShareV2
      ? {}
      : {
          saveFedShareDataPath: this.props.saveFedShareDataPath,
          saveAppShareDataPath: this.props.saveAppShareDataPath,
          fedShareRate: this.props.fedShareRate,
          appShareRate: this.props.appShareRate,
        };

    return this.props.config.contents.map((entry, index) => {
      return (
        <React.Fragment>
          <EligibleItemsContext.Provider value={contextValue}>
            <ContentDispatch
              config={entry}
              key={'RequestDetailsControllerV3'}
              entityDataPathIndx={this.props.entityDataPathIndx}
              entityDataPath={this.props.entityDataPath1}
              entityTypeReduxPath={this.props.entityTypeReduxPath}
              handlePostSave={this.props.handlePostSave}
              entityDataPath1={this.props.entityDataPath1}
              saveTotalDataPath={this.props.saveTotalDataPath}
              applicationType={this.props.applicationType}
              entityType={this.props.entityType}
              skipOverrideTotal={true}
              hasEligibleChildren={false}
              previousRequestDetails={this.state.previousRequestDetails}
              modeName={this.props.modeName}
              editable={this.props.editable}
              {...additionalProps}
            />
          </EligibleItemsContext.Provider>
        </React.Fragment>
      );
    });
  }
}
export const mapStateToProps = (state) => ({
  token: state.auth.token,
  eligibility: state.dynamicComponent.context.eligibility,
});

export const mapDispatchToProps = (dispatch) => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(connectDynamic(SubAwardRecommendationListener));
